import React, { useState } from 'react';
import { X, Copy, Check, Mail, ExternalLink, Download, Facebook, MessageCircle } from 'lucide-react';
import { useToast } from '../../../../hooks/useToast';

interface ChartShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareableUrl: string;
  onExport: () => void;
  darkMode: boolean;
  chartTitle: string;
  isExporting: boolean;
  chartImageData?: string;
}

const ChartShareModal: React.FC<ChartShareModalProps> = ({ isOpen, onClose, shareableUrl, onExport, darkMode, chartTitle, isExporting, chartImageData }) => {
  const [copied, setCopied] = useState(false);
  const { success } = useToast();

  const copyToClipboard = async () => {
    try { await navigator.clipboard.writeText(shareableUrl); setCopied(true); success('Lien copié !', 'Le lien a été copié dans le presse-papiers'); setTimeout(() => setCopied(false), 2000); } catch (err) { console.error('Failed to copy:', err); }
  };

  const shareByEmail = () => {
    const subject = encodeURIComponent(`${chartTitle} - Simulation NGAP`);
    const body = encodeURIComponent(`Bonjour,\n\nJe partage avec vous ma simulation d'évolution des revenus NGAP :\n\n${chartTitle}\n\nVoir la simulation : ${shareableUrl}\n\nCordialement`);
    if (chartImageData) { const link = document.createElement('a'); link.download = `evolution-revenus-ngap-${new Date().toISOString().split('T')[0]}.png`; link.href = chartImageData; link.click(); setTimeout(() => { window.open(`mailto:?subject=${subject}&body=${body}`); }, 500); }
    else { window.open(`mailto:?subject=${subject}&body=${body}`); }
  };

  const shareOnLinkedIn = () => { window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareableUrl)}`, '_blank'); };
  const shareOnTwitter = () => { const text = encodeURIComponent(`${chartTitle} - Simulation NGAP`); window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareableUrl)}&hashtags=${encodeURIComponent('NGAP,kinésithérapie')}`, '_blank'); };
  const shareOnFacebook = () => { window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareableUrl)}`, '_blank'); };
  const shareOnWhatsApp = () => { const text = encodeURIComponent(`${chartTitle}\n\nVoir la simulation : ${shareableUrl}`); window.open(`https://wa.me/?text=${text}`, '_blank'); };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-appines-black/50" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-xl shadow-2xl ${darkMode ? 'bg-appines-petrol' : 'bg-appines-white'}`}>
        <div className={`flex items-center justify-between p-6 border-b ${darkMode ? 'border-appines-white/12' : 'border-[rgba(1,42,54,0.12)]'}`}>
          <h3 className={`text-lg font-bold ${darkMode ? 'text-appines-white' : 'text-appines-petrol'}`}>Partager le Graphique</h3>
          <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${darkMode ? 'text-appines-white/65 hover:text-appines-white hover:bg-appines-white/10' : 'text-appines-petrol/65 hover:text-appines-petrol hover:bg-[rgba(1,42,54,0.06)]'}`}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}`}>Lien de partage</label>
            <div className="flex items-center space-x-2">
              <input type="text" value={shareableUrl} readOnly
                className={`flex-1 px-3 py-2 text-sm border rounded-lg ${darkMode ? 'bg-appines-white/5 border-appines-white/12 text-appines-white/65' : 'bg-[rgba(1,42,54,0.04)] border-[rgba(1,42,54,0.12)] text-appines-petrol/65'}`} />
              <button onClick={copyToClipboard}
                className="px-4 py-2 bg-appines-emerald hover:bg-[#22b886] text-appines-petrol rounded-lg transition-colors flex items-center space-x-2">
                {copied ? <><Check className="w-4 h-4" /><span className="text-sm">Copié</span></> : <><Copy className="w-4 h-4" /><span className="text-sm">Copier</span></>}
              </button>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-3 ${darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}`}>Partager via</label>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={shareByEmail} className={`flex items-center justify-center space-x-2 p-3 rounded-lg border transition-colors ${darkMode ? 'border-appines-white/12 bg-appines-white/5 text-appines-white/65 hover:bg-appines-white/10' : 'border-[rgba(1,42,54,0.12)] bg-[rgba(1,42,54,0.04)] text-appines-petrol/65 hover:bg-[rgba(1,42,54,0.08)]'}`}>
                <Mail className="w-4 h-4" /><span className="text-sm">Email</span>
              </button>
              <button onClick={shareOnLinkedIn} className="flex items-center justify-center space-x-2 p-3 bg-[#0077B5] hover:bg-[#005f91] text-white rounded-lg transition-colors">
                <ExternalLink className="w-4 h-4" /><span className="text-sm">LinkedIn</span>
              </button>
              <button onClick={shareOnTwitter} className="flex items-center justify-center space-x-2 p-3 bg-appines-sky hover:bg-appines-sky/80 text-appines-petrol rounded-lg transition-colors">
                <ExternalLink className="w-4 h-4" /><span className="text-sm">Twitter</span>
              </button>
              <button onClick={shareOnFacebook} className="flex items-center justify-center space-x-2 p-3 bg-[#1877F2] hover:bg-[#1565C0] text-white rounded-lg transition-colors">
                <Facebook className="w-4 h-4" /><span className="text-sm">Facebook</span>
              </button>
              <button onClick={shareOnWhatsApp} className="flex items-center justify-center space-x-2 p-3 bg-appines-emerald hover:bg-[#22b886] text-appines-petrol rounded-lg transition-colors">
                <MessageCircle className="w-4 h-4" /><span className="text-sm">WhatsApp</span>
              </button>
              <button onClick={onExport} disabled={isExporting}
                className={`flex items-center justify-center space-x-2 p-3 rounded-lg transition-colors ${isExporting ? 'opacity-50 cursor-not-allowed bg-appines-petrol/30' : 'bg-appines-petrol hover:bg-appines-black text-appines-white'}`}>
                <Download className={`w-4 h-4 ${isExporting ? 'animate-pulse' : ''}`} /><span className="text-sm">PNG</span>
              </button>
            </div>
          </div>

          <div className={`p-3 rounded-lg text-xs ${darkMode ? 'bg-appines-white/5 text-appines-white/45' : 'bg-[rgba(1,42,54,0.04)] text-appines-petrol/45'}`}>
            <p className="text-center">Le lien partagé permettra de reproduire votre simulation avec les mêmes paramètres</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChartShareModal;
